import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
  Play,
  Clock,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  BookOpen,
  Zap,
  Award,
  Calendar,
  Tag,
  Loader2,
  ListVideo,
  User,
  Info,
  ShieldCheck,
  Globe,
  Lock,
  CheckCircle,
  Video as VideoIcon,
  FileText,
  HelpCircle,
  MessageSquare,
} from 'lucide-react';
import VideoPlayer from './VideoPlayer';
import PaymentRequestModal from '../payment/PaymentRequestModal';
import CourseReviews, { StarRow } from './CourseReviews';
import {
  useGetPreviewVideoQuery,
  useCheckCourseAccessQuery,
  useGetPurchasedCourseContentQuery,
} from '../../api/apiSlice';
import { AVATAR_FALLBACK, setFallback } from '../../lib/placeholders';
import { computePricing } from '../../lib/coursePricing';

const CourseDetail = ({ course }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth);

  const [previewVideoUrl, setPreviewVideoUrl] = useState(null);
  // Initial tab — из ?tab=reviews если есть (deep-link с completion-баннера
  // в CourseViewer). Иначе дефолт «Программа». Поддерживаем только известные id.
  const initialTab = (() => {
    const t = searchParams.get('tab');
    return ['curriculum', 'instructor', 'reviews'].includes(t) ? t : 'curriculum';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [openSectionIndex, setOpenSectionIndex] = useState(0);
  const [purchasedContent, setPurchasedContent] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const {
    data: previewData,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useGetPreviewVideoQuery(course?._id, {
    skip: !course?._id,
  });

  const {
    data: accessData,
    isLoading: isAccessLoading,
  } = useCheckCourseAccessQuery(course?._id, {
    skip: !course?._id || !user,
  });

  const { data: contentData } = useGetPurchasedCourseContentQuery(course?._id, {
    skip: !course?._id || !user || !accessData?.hasAccess,
  });

  const hasAccess = accessData?.hasAccess || false;

  useEffect(() => {
    if (previewData?.url) setPreviewVideoUrl(previewData.url);
  }, [previewData]);

  useEffect(() => {
    if (contentData?.data) {
      setPurchasedContent(contentData.data);
    }
  }, [contentData]);

  const sections = useMemo(
    () =>
      hasAccess && purchasedContent
        ? purchasedContent.sections || []
        : course?.sections || [],
    [hasAccess, purchasedContent, course?.sections]
  );

  const sectionsCount = sections.length;

  const lessonsCount = useMemo(
    () =>
      sections.reduce(
        (total, section) => total + (section?.lessons?.length || 0),
        0
      ),
    [sections]
  );

  const totalDurationMinutes = useMemo(
    () =>
      sections.reduce(
        (total, section) =>
          total +
          (section?.lessons?.reduce(
            (sum, lesson) => sum + (lesson?.durationMinutes || 0),
            0
          ) || 0),
        0
      ),
    [sections]
  );

  // Счётчики по типам — нужны для бейджей «X видео · Y теории · Z квизов»
  // в шапке. Студент сразу понимает структуру курса до покупки.
  const typeCounts = useMemo(() => {
    const c = { video: 0, theory: 0, quiz: 0 };
    for (const s of sections) {
      for (const l of s?.lessons || []) {
        const t = l?.type || 'video';
        if (c[t] !== undefined) c[t] += 1;
      }
    }
    return c;
  }, [sections]);

  const totalDurationHours = Math.floor(totalDurationMinutes / 60);
  const remainingMinutes = totalDurationMinutes % 60;

  const formattedDuration =
    totalDurationHours > 0
      ? `${totalDurationHours} ч ${remainingMinutes} мин`
      : `${remainingMinutes} мин`;

  const toggleSection = (index) => {
    setOpenSectionIndex((prev) => (prev === index ? null : index));
  };

  const handleBuyCourse = () => {
    if (!user) {
      toast.error('Пожалуйста, войдите в аккаунт');
      navigate('/auth');
      return;
    }

    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    toast.success('Курс успешно куплен! Вы можете начать обучение.');
    setShowPaymentModal(false);
  };

  const handleViewProfile = () => {
    if (course.instructor?._id) {
      navigate(`/instructor/${course.instructor._id}`);
    }
  };

  if (!course || course.status !== 'active') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg rounded-3xl border border-red-200 dark:border-red-900/40 bg-white dark:bg-gray-900 p-8 text-center shadow-sm">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50 dark:bg-red-950/30">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Курс недоступен
          </h2>
          <p className="mt-3 text-sm leading-7 text-gray-500 dark:text-gray-400">
            К сожалению, этот курс сейчас неактивен или временно недоступен.
          </p>
        </div>
      </div>
    );
  }

  // Единый helper для всех мест где показывается цена курса. discountPrice
  // в БД может быть устаревшим — пересчитываем на лету из discount %.
  const { basePrice, finalPrice: displayPrice, discountPercent, hasDiscount } = computePricing(course);

  const publicationDate = course.publishedAt
    ? new Date(course.publishedAt).toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  // Убрали отдельную «О курсе» — описание/детали/бенефиты теперь в «Программе»
  // на одном экране. Меньше кликов, выше глубина просмотра.
  // Звёзды + счётчик отзывов в шапке — серверные денормализованные поля
  // на Course. Не делаем доп. запрос на каталоге.
  const avgRating = Number(course.avgRating) || 0;
  const reviewsCount = Number(course.reviewsCount) || 0;

  const tabs = [
    { id: 'curriculum', label: 'Программа', icon: ListVideo },
    { id: 'instructor', label: 'Автор', icon: User },
    { id: 'reviews', label: 'Отзывы', icon: MessageSquare },
  ];

  const cardClass =
    'rounded-3xl border border-gray-200/70 dark:border-gray-700/70 bg-white dark:bg-gray-900 shadow-sm';

  const softCardClass =
    'rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-gray-50/80 dark:bg-gray-800/50';

  const tabBtnClass =
    'flex min-w-max items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium transition';

  return (
    <>
      {/* pb-28 на мобилке резервирует место под mobile sticky CTA bar внизу,
          иначе нижний контент уезжает под фиксированную панель.
          Убрали min-h-screen — компонент рендерится внутри DashboardLayout
          main, который сам растягивается. Лишний min-h-screen создавал
          двойной "пустой экран" эффект на коротких курсах. */}
      <div className="bg-gray-50 dark:bg-gray-950 py-4 sm:py-8 lg:py-10 pb-28 lg:pb-10 overflow-x-hidden">
        <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 lg:px-8">
          {/* min-w-0 на grid-детях — обязательно для flexbox/grid внутри
              узких вьюпортов. Без него любая длинная строка (URL,
              слово без пробелов, broad-flex) тянет колонку шире 100%,
              и правый край уезжает за экран. */}
          <div className="grid gap-6 sm:gap-8 lg:grid-cols-12 min-w-0">
            <div className="lg:col-span-8 space-y-6 min-w-0">
              <div className={`${cardClass} overflow-hidden`}>
                <div className="border-b border-gray-100 dark:border-gray-800 px-5 py-6 sm:px-7 sm:py-7">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900 dark:text-white break-words">
                    {course.title}
                  </h1>

                  {/* Рейтинг + счётчик отзывов сразу под заголовком — это
                      первое что видит потенциальный покупатель. Кликабельно
                      → переход на таб «Отзывы». */}
                  {reviewsCount > 0 && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('reviews')}
                      className="mt-3 inline-flex items-center gap-2 group"
                    >
                      <StarRow value={avgRating} size="md" />
                      <span className="text-base font-bold text-gray-900 dark:text-white tabular-nums">
                        {avgRating.toFixed(1)}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 underline-offset-2 group-hover:underline transition">
                        ({reviewsCount} {reviewsCount === 1 ? 'отзыв' : 'отзывов'})
                      </span>
                    </button>
                  )}

                  <div className="mt-4 flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 dark:bg-indigo-950/30 px-3 py-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300">
                      <BookOpen className="h-4 w-4" />
                      {lessonsCount} уроков
                    </div>

                    <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 dark:bg-blue-950/30 px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300">
                      <Clock className="h-4 w-4" />
                      {formattedDuration}
                    </div>

                    <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                      <ListVideo className="h-4 w-4" />
                      {sectionsCount} разделов
                    </div>

                    <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Globe className="h-4 w-4" />
                      {course.language || 'Казахский'}
                    </div>
                  </div>
                </div>

                <div className="px-4 py-4 sm:px-5">
                  <div className="flex gap-2 overflow-x-auto rounded-2xl bg-gray-100 dark:bg-gray-800 p-1.5">
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.id;

                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`${tabBtnClass} ${
                            isActive
                              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                  {activeTab === 'curriculum' && (
                    <div className="space-y-6">
                      {/* About — раньше была отдельная вкладка «О курсе»,
                          теперь идёт сразу над программой, чтобы не прятать. */}
                      <div className={`${cardClass} p-5 sm:p-6`}>
                        <h3 className="flex items-center gap-2 text-xl font-semibold text-gray-900 dark:text-white">
                          <Info className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                          Описание курса
                        </h3>
                        <p className="mt-3 whitespace-pre-line break-words [overflow-wrap:anywhere] text-sm sm:text-base leading-7 text-gray-600 dark:text-gray-300">
                          {course.description || 'Описание курса отсутствует.'}
                        </p>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div className={`${cardClass} p-5 sm:p-6`}>
                          <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                            Детали
                          </h4>
                          <div className="mt-4 space-y-3">
                            <div className="flex items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-800 pb-3">
                              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                <Clock className="h-4 w-4" /> Длительность
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {formattedDuration}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-800 pb-3">
                              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                <BookOpen className="h-4 w-4" /> Уроков
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {lessonsCount}
                              </span>
                            </div>
                            <div className="flex items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-800 pb-3">
                              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                <Globe className="h-4 w-4" /> Язык
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {course.language || 'Казахский'}
                              </span>
                            </div>
                            {Array.isArray(course.category) && course.category.length > 0 ? (
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                  <Tag className="h-4 w-4" /> Категория
                                </div>
                                <span className="text-right text-sm font-medium text-gray-900 dark:text-white break-words">
                                  {course.category.map((c) => c.title).join(', ')}
                                </span>
                              </div>
                            ) : course.category?.title ? (
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                                  <Tag className="h-4 w-4" /> Категория
                                </div>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {course.category.title}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="rounded-3xl border border-emerald-200/70 dark:border-emerald-900/30 bg-emerald-50/60 dark:bg-emerald-950/10 p-5 sm:p-6 shadow-sm">
                          <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                            Что вы получите
                          </h4>
                          <ul className="mt-4 space-y-3">
                            {[
                              'Пожизненный доступ к курсу',
                              'Сертификат об окончании',
                              'Доступ с любых устройств',
                              'Поддержка от автора',
                            ].map((item, idx) => (
                              <li key={idx} className="flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
                                <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                                  ✓
                                </div>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* hasAccess → юзер УЖЕ купил курс. Подробное содержимое
                          с видео-плеерами и драгом по урокам в публичной деталке
                          не нужно — это маркетинг для НЕпокупателей. Купившему
                          студенту показываем чёткое CTA «к обучению», ведёт
                          в полноценный CourseViewer. */}
                      {hasAccess ? (
                        <div className="rounded-3xl border border-emerald-200/70 dark:border-emerald-900/40 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/30 dark:to-gray-900 p-6 sm:p-8 shadow-sm">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-emerald-500 text-white">
                              <CheckCircle className="h-7 w-7" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                                Вы уже купили этот курс
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                Все уроки и материалы открыты. Перейдите к обучению, чтобы
                                продолжить с того места, где остановились.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => navigate(`/learn/${course._id}`)}
                              className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3 text-sm shrink-0 shadow-sm"
                            >
                              <Play className="w-4 h-4" />
                              Перейти к обучению
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pt-2">
                        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
                          Содержание курса
                        </h2>

                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1.5">
                            <BookOpen className="h-4 w-4" />
                            {lessonsCount} уроков
                          </span>

                          <span className="flex items-center gap-1.5">
                            <Clock className="h-4 w-4" />
                            {formattedDuration}
                          </span>
                        </div>
                      </div>

                      {/* Type-breakdown бейджи. Студент сразу видит структуру:
                          сколько видео, сколько теории, сколько квизов. До покупки
                          это критично для решения — иначе непонятно за что
                          платишь. Скрываем нулевые типы чтобы не визуально шумить. */}
                      {(typeCounts.video > 0 || typeCounts.theory > 0 || typeCounts.quiz > 0) && (
                        <div className="flex flex-wrap items-center gap-2">
                          {typeCounts.video > 0 && (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-3 py-1 text-xs font-medium">
                              <VideoIcon className="h-3.5 w-3.5" />
                              {typeCounts.video} видео
                            </span>
                          )}
                          {typeCounts.theory > 0 && (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 px-3 py-1 text-xs font-medium">
                              <FileText className="h-3.5 w-3.5" />
                              {typeCounts.theory} теории
                            </span>
                          )}
                          {typeCounts.quiz > 0 && (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 px-3 py-1 text-xs font-medium">
                              <HelpCircle className="h-3.5 w-3.5" />
                              {typeCounts.quiz} {typeCounts.quiz === 1 ? 'квиз' : 'квизов'}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                        {sections.length > 0 ? (
                          sections.map((section, index) => (
                            <div
                              key={index}
                              className="border-b last:border-b-0 border-gray-200 dark:border-gray-700"
                            >
                              <button
                                onClick={() => toggleSection(index)}
                                className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition hover:bg-gray-50 dark:hover:bg-gray-800"
                              >
                                <div className="flex min-w-0 items-center gap-4">
                                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white font-semibold">
                                    {index + 1}
                                  </div>

                                  <div className="min-w-0">
                                    <h4 className="truncate text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                                      {section.title}
                                    </h4>
                                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                      {section.lessons?.length || 0} уроков
                                    </p>
                                  </div>
                                </div>

                                {openSectionIndex === index ? (
                                  <ChevronUp className="h-5 w-5 shrink-0 text-indigo-600 dark:text-indigo-400" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 shrink-0 text-gray-400" />
                                )}
                              </button>

                              {openSectionIndex === index && (
                                <div className="px-4 pb-4">
                                  <div className="space-y-2 border-l-2 border-indigo-200 dark:border-indigo-800 pl-4 sm:pl-5">
                                    {section.lessons?.length > 0 ? (
                                      section.lessons.map(
                                        (lesson, lessonIndex) => (
                                          <div key={lessonIndex}>
                                            {hasAccess && lesson.videoUrl ? (
                                              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 overflow-hidden mb-2">
                                                <div className="aspect-video bg-black">
                                                  <VideoPlayer
                                                    videoUrl={lesson.videoUrl}
                                                    title={lesson.title}
                                                  />
                                                </div>

                                                <div className="p-4">
                                                  <h5 className="font-semibold text-gray-900 dark:text-white">
                                                    {lessonIndex + 1}.{' '}
                                                    {lesson.title}
                                                  </h5>

                                                  {lesson.description && (
                                                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                                                      {lesson.description}
                                                    </p>
                                                  )}

                                                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                                    Длительность:{' '}
                                                    {lesson.durationMinutes || 0}{' '}
                                                    мин
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              (() => {
                                                // Полиморфный type-icon + бейдж типа. Не-видео урок
                                                // показывает «Теория»/«Квиз» вместо «N мин».
                                                const lessonType = lesson.type || 'video';
                                                const TypeIcon =
                                                  lessonType === 'theory'
                                                    ? FileText
                                                    : lessonType === 'quiz'
                                                    ? HelpCircle
                                                    : hasAccess
                                                    ? CheckCircle
                                                    : Play;
                                                const iconWrapColor =
                                                  lessonType === 'theory'
                                                    ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                                                    : lessonType === 'quiz'
                                                    ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400'
                                                    : 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400';
                                                return (
                                              <div
                                                className={`flex flex-col gap-2 rounded-xl px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
                                                  hasAccess
                                                    ? 'bg-indigo-50 dark:bg-indigo-900/20'
                                                    : 'bg-gray-50 dark:bg-gray-800/60'
                                                }`}
                                              >
                                                <div className="flex items-start gap-3">
                                                  <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconWrapColor}`}>
                                                    <TypeIcon className="h-4 w-4" />
                                                  </div>

                                                  <div className="text-sm sm:text-base text-gray-800 dark:text-gray-200">
                                                    {lessonIndex + 1}.{' '}
                                                    {lesson.title}
                                                  </div>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                  {!hasAccess && (
                                                    <Lock className="h-4 w-4 text-amber-500" />
                                                  )}

                                                  <div className="inline-flex items-center self-start rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3 py-1 text-sm font-medium text-gray-600 dark:text-gray-300 sm:self-auto">
                                                    {lessonType === 'theory'
                                                      ? 'Теория'
                                                      : lessonType === 'quiz'
                                                      ? 'Квиз'
                                                      : `${lesson.durationMinutes || 0} мин`}
                                                  </div>
                                                </div>
                                              </div>
                                                );
                                              })()
                                            )}
                                          </div>
                                        )
                                      )
                                    ) : (
                                      <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                                        Уроки пока не добавлены
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                            Программа курса пока не заполнена
                          </div>
                        )}
                      </div>
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'reviews' && (
                    <CourseReviews courseId={course._id} />
                  )}

                  {activeTab === 'instructor' && (
                    <div className="grid gap-5 md:grid-cols-3">
                      <div className="md:col-span-1">
                        {/* Вся карточка автора кликабельна — ведёт на публичный
                            профиль инструктора. Раньше работала только кнопка. */}
                        <button
                          type="button"
                          onClick={handleViewProfile}
                          disabled={!course.instructor?._id}
                          className={`${softCardClass} p-5 text-center h-full w-full transition hover:shadow-md hover:bg-white dark:hover:bg-gray-800/80 disabled:cursor-default disabled:hover:shadow-none`}
                        >
                          <img
                            src={course.instructor?.avatarUrl || AVATAR_FALLBACK}
                            alt={course.instructorProfile?.publicName || course.instructor?.name}
                            loading="lazy"
                            onError={setFallback(AVATAR_FALLBACK)}
                            className="mx-auto h-24 w-24 sm:h-28 sm:w-28 rounded-full object-cover border-4 border-white dark:border-gray-700 shadow-sm"
                          />

                          <h3 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white break-words">
                            {course.instructorProfile?.publicName ||
                              course.instructor?.name ||
                              'Неизвестно'}
                          </h3>

                          <p className="mt-1 text-sm font-medium text-indigo-600 dark:text-indigo-400 break-words">
                            {course.instructorProfile?.specialization || 'Инструктор'}
                          </p>

                          <span className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gray-900 dark:bg-white px-4 py-3 text-sm font-medium text-white dark:text-gray-900 transition hover:bg-black dark:hover:bg-gray-200">
                            Смотреть профиль
                          </span>
                        </button>
                      </div>

                      <div className="md:col-span-2">
                        <div className={`${cardClass} p-5 sm:p-6 h-full`}>
                          <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Об авторе
                          </h4>

                          {course.instructorProfile?.bio ? (
                            <p className="mt-4 whitespace-pre-line break-words [overflow-wrap:anywhere] text-sm sm:text-base leading-7 text-gray-600 dark:text-gray-300">
                              {course.instructorProfile.bio}
                            </p>
                          ) : (
                            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 italic">
                              Биография инструктора пока не добавлена
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>

            <div className="lg:col-span-4 space-y-6 min-w-0">
              {/* Превью-карточка с ценой видна и на мобилке (нужна для самого
                  превью-видео), но кнопка покупки/CTA здесь скрыта на мобилке —
                  она вынесена в sticky bottom bar для лучшей конверсии. */}
              <div className={`${cardClass} overflow-hidden lg:sticky lg:top-6`}>
                <div className="relative aspect-video bg-gray-100 dark:bg-gray-800">
                  {isPreviewLoading ? (
                    <div className="flex h-full w-full flex-col items-center justify-center">
                      <Loader2 className="mb-3 h-8 w-8 animate-spin text-indigo-600" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Загрузка видео...
                      </span>
                    </div>
                  ) : previewVideoUrl ? (
                    <VideoPlayer
                      videoUrl={previewVideoUrl}
                      posterUrl={course.coverImageUrl}
                      title={`Превью курса: ${course.title}`}
                    />
                  ) : isPreviewError ? (
                    <div className="flex h-full w-full flex-col items-center justify-center px-4 text-center">
                      <AlertTriangle className="mb-3 h-10 w-10 text-amber-500" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Видео недоступно
                      </span>
                    </div>
                  ) : (
                    <img
                      src={course.coverImageUrl}
                      alt={course.title}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>

                <div className="p-5 sm:p-6">
                  <div className="mb-5 flex items-end justify-between gap-4">
                    <div>
                      {hasDiscount ? (
                        <>
                          <p className="mb-1 text-sm text-gray-400 line-through">
                            {basePrice.toLocaleString('ru-RU')} ₸
                          </p>

                          <p className="text-3xl sm:text-4xl font-bold tracking-tight text-emerald-600 dark:text-emerald-400">
                            {displayPrice.toLocaleString('ru-RU')} ₸
                          </p>

                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Экономия {discountPercent}%
                          </p>
                        </>
                      ) : (
                        <p className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
                          {displayPrice.toLocaleString('ru-RU')} ₸
                        </p>
                      )}
                    </div>

                    {hasDiscount && (
                      <div className="inline-flex items-center gap-1 rounded-full bg-red-50 dark:bg-red-950/30 px-3 py-1.5 text-sm font-semibold text-red-600 dark:text-red-400">
                        <Zap className="h-4 w-4" />
                        SALE
                      </div>
                    )}
                  </div>

                  {hasAccess ? (
                    <div className="w-full rounded-2xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/50 px-5 py-3.5 text-base font-semibold text-emerald-700 dark:text-emerald-300 flex items-center justify-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Вы уже приобрели этот курс
                    </div>
                  ) : (
                    <button
                      onClick={handleBuyCourse}
                      disabled={isAccessLoading}
                      className="w-full rounded-2xl bg-indigo-600 px-5 py-3.5 text-base font-semibold text-white transition hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      Купить сейчас
                    </button>
                  )}

                  <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <ShieldCheck className="h-4 w-4" />
                    Безопасная оплата
                  </div>

                  <div className="mt-5 space-y-3 border-t border-gray-100 dark:border-gray-800 pt-5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        Уроков
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {lessonsCount}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        Длительность
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formattedDuration}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        Разделов
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {sectionsCount}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        Язык
                      </span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {course.language || 'Казахский'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {publicationDate && (
                <div className={`${cardClass} p-5`}>
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-50 dark:bg-blue-950/30">
                      <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        Дата публикации
                      </h4>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {publicationDate}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-3xl border border-amber-200/70 dark:border-amber-900/30 bg-amber-50/70 dark:bg-amber-950/10 p-5 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-100 dark:bg-amber-900/40">
                    <Award className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      Сертификат
                    </h4>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      После завершения курса
                    </p>
                    <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
                      После прохождения всех уроков вы получите сертификат об
                      окончании курса.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Mobile sticky CTA ──────────────────────────────────────────────
          На lg+ скрыта (там sticky sidebar справа уже всё показывает).
          На мобилке — фиксированная панель внизу с ценой и главной кнопкой.
          Без неё юзер должен проскроллить весь курс чтобы найти «Купить»,
          конверсия страдает.
          - pb-safe — учёт iOS home-indicator (env(safe-area-inset-bottom))
          - backdrop-blur+полупрозрачный фон — контент под ней слегка
            проглядывает, не выглядит как отрубленный footer. */}
      <div
        className="lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:supports-[backdrop-filter]:bg-gray-900/80 shadow-[0_-4px_16px_-4px_rgba(0,0,0,0.08)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6">
          <div className="min-w-0 flex-1">
            {hasDiscount ? (
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-bold tracking-tight text-emerald-600 dark:text-emerald-400 tabular-nums">
                  {displayPrice.toLocaleString('ru-RU')} ₸
                </span>
                <span className="text-xs text-gray-400 line-through tabular-nums">
                  {basePrice.toLocaleString('ru-RU')} ₸
                </span>
                <span className="ml-auto inline-flex shrink-0 items-center gap-0.5 rounded-full bg-red-50 dark:bg-red-950/30 px-2 py-0.5 text-[11px] font-semibold text-red-600 dark:text-red-400 sm:ml-1">
                  -{discountPercent}%
                </span>
              </div>
            ) : (
              <span className="text-lg font-bold tracking-tight text-gray-900 dark:text-white tabular-nums">
                {displayPrice.toLocaleString('ru-RU')} ₸
              </span>
            )}
            <div className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400">
              {lessonsCount} уроков · {formattedDuration}
            </div>
          </div>

          {hasAccess ? (
            <button
              type="button"
              onClick={() => navigate(`/learn/${course._id}`)}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 active:scale-[0.98] transition"
            >
              <Play className="h-4 w-4" />
              К обучению
            </button>
          ) : (
            <button
              type="button"
              onClick={handleBuyCourse}
              disabled={isAccessLoading}
              className="inline-flex shrink-0 items-center justify-center rounded-2xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Купить
            </button>
          )}
        </div>
      </div>

      <PaymentRequestModal
        isOpen={showPaymentModal}
        course={course}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
      />
    </>
  );
};

export default CourseDetail;